import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import apiConfig from "../../../apiConfig";

function Processing() {
    const navigate = useNavigate();
    const [orderData, setOrderData] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [orderIdFilter, setOrderIdFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("processing");
    const [loading, setLoading] = useState(false);
    const [startDateFilter, setStartDateFilter] = useState("");
    const [endDateFilter, setEndDateFilter] = useState("");
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [bulkStatus, setBulkStatus] = useState("");

    const fetchOrders = async (status, page = 0) => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${apiConfig.baseUrl}${apiConfig.ProcessingData}`
            );
            if (response.data && response.data) {
                setOrderData(response.data);
                setFilteredOrders(response.data);
            }
        } catch (error) {
            setLoading(true);
            console.error(
                "Error fetching orders:",
                error.response?.data || error.message
            );
            alert("Failed to fetch orders. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handlePageClick = (event) => {
        const selectedPage = event.selected;
        fetchOrders(statusFilter, selectedPage);
    };

    const exportFullOrderDetailsToExcel = (
        orders,
        filename = "All_Orders.xlsx"
    ) => {
        const allRows = [];

        orders?.forEach((order, index) => {
            order?.items?.forEach((item, itemIndex) => {
                allRows.push({
                    "S.No": index + 1,
                    "Order ID": order?.orderId,
                    "User Name": `${order?.items?.firstName || ""} ${order?.items?.lastName || ""
                        }`.trim(),

                    "User Email": order?.items?.email || "",
                    Phone: order?.items?.phone || "N/A",
                    "Full Address": `${order?.items?.streetAddress || ""}, ${order?.items?.city || ""
                        }, ${order?.items?.state || ""} - ${order?.items?.pinCode || ""
                        }`,

                    "Order Date": new Date(order?.orderDate).toLocaleDateString("en-IN"),
                    "Order Time": new Date(order?.orderDate).toLocaleTimeString("en-IN", {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                        timeZone: "Asia/Kolkata",
                    }),
                    "Product Name": item.title || "",
                    "Product ID": item.productId || "",
                    Quantity: item.quantity || 1,
                    Price: item.price?.toFixed(2) || "0.00",
                    "Shipping Cost": order.shippingCost || "0.00",
                    Total: (item.price * item.quantity)?.toFixed(2) || "0.00",
                    Status: order?.status || "",
                });
            });
        });

        const worksheet = XLSX.utils.json_to_sheet(allRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");

        XLSX.writeFile(workbook, filename);
    };

    useEffect(() => {
        const filtered = orderData.filter((order) => {
            const matchesOrderId =
                orderIdFilter === "" ||
                order?.orderId?.toLowerCase().includes(orderIdFilter?.toLowerCase());
            const matchesStatus =
                statusFilter === "processing" ||
                order.status?.toLowerCase() === statusFilter?.toLowerCase();
            const orderDate = new Date(order.orderDate);
            const start = startDateFilter ? new Date(startDateFilter) : null;
            const end = endDateFilter ? new Date(endDateFilter) : null;

            const matchesDateRange =
                (!start || orderDate >= start) && (!end || orderDate <= end);

            return matchesOrderId && matchesStatus && matchesDateRange;
        });

        setFilteredOrders(filtered);
    }, [orderIdFilter, statusFilter, startDateFilter, endDateFilter, orderData]);

    useEffect(() => {
        fetchOrders("processing");
    }, []);

    const updateOrderStatus = async (orderId, newStatus) => {
        try {
            setLoading(true);

            const response = await axios.put(
                `${apiConfig.baseUrl}${apiConfig.UpdateOrderStatus}`,
                { orderId: orderId, newStatus: newStatus }
            );

            if (response.data) {
                fetchOrders(statusFilter);
                alert(`Order status updated to ${newStatus}`);
            }
        } catch (error) {
            console.error(
                "Error updating order status:",
                error.response?.data || error.message
            );
            alert("Failed to update order status. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = (e) => {
        const selectedStatus = e.target.value;
        setStatusFilter(selectedStatus);
        fetchOrders(selectedStatus);
    };

    const formatDate = (isoDateString) => {
        const date = new Date(isoDateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const formatTime = (isoDateString) => {
        const date = new Date(isoDateString);
        return date.toLocaleTimeString("en-IN", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            timeZone: "Asia/Kolkata",
        });
    };

    const groupedOrders = Object.values(
        filteredOrders?.reduce((acc, order) => {
            if (!acc[order.orderId]) {
                acc[order.orderId] = {
                    ...order,
                    items: [...order.items],
                };
            } else {
                acc[order.orderId].items.push(...order.items);
            }
            return acc;
        }, {})
    );

    const handleBulkUpdate = async () => {
        if (selectedOrders.length === 0 || !bulkStatus) {
            alert("⚠️ Please select at least one order and a status.");
            return;
        }

        try {
            setLoading(true);
            console.log("🟡 Starting bulk update...");
            console.log("Selected Orders:", selectedOrders);
            console.log("New Status to Apply:", bulkStatus);

            const response = await axios.put(
                `${apiConfig.baseUrl}/superAdmin/Bulk-Update-order`,
                {
                    orderIds: selectedOrders,
                    newStatus: bulkStatus,
                }
            );

            console.log("✅ API Response:", response.data);

            if (response.data) {
                alert("✅ Order statuses updated successfully!");
                // refresh the order list
                fetchOrders(statusFilter);
                // reset state
                setSelectedOrders([]);
                setBulkStatus("");
            }
        } catch (error) {
            console.error("❌ Error updating orders:", error.response?.data || error);
            alert("Failed to update order statuses. Please try again.");
        } finally {
            setLoading(false);
            console.log("🟢 Bulk update process completed.");
        }
    };

    return (
        <div style={{ fontFamily: "Arial, sans-serif", padding: "20px" }}>
            <h1 style={{ color: "#333", fontSize: "24px", marginBottom: "15px" }}>
                Orders
            </h1>

            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "15px",
                    backgroundColor: "#fff",
                    padding: "20px",
                    borderRadius: "12px",
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.08)",
                    marginBottom: "25px",
                }}
            >
                {/* ===== Row 1: Filters ===== */}
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "15px",
                        alignItems: "flex-end",
                    }}
                >
                    {/* Order ID */}
                    <div style={{ flex: "1 1 200px" }}>
                        <label style={{ fontWeight: "600", color: "#333" }}>Order ID</label>
                        <input
                            type="text"
                            value={orderIdFilter}
                            onChange={(e) => setOrderIdFilter(e.target.value)}
                            placeholder="Enter Order ID"
                            style={{
                                width: "80%",
                                marginTop: "5px",
                                padding: "10px",
                                borderRadius: "6px",
                                border: "1px solid #ccc",
                                fontSize: "14px",
                            }}
                        />
                    </div>

                    {/* Status */}
                    <div style={{ flex: "1 1 200px" }}>
                        <label style={{ fontWeight: "600", color: "#333" }}>Status</label>
                        <select
                            value={statusFilter}
                            onChange={handleStatusChange}
                            style={{
                                width: "90%",
                                marginTop: "5px",
                                padding: "10px",
                                borderRadius: "6px",
                                border: "1px solid #ccc",
                                fontSize: "14px",
                                backgroundColor: "#fff",
                                lineHeight: "1.5",
                            }}
                        >
                            {/* General */}
                            <option value="all">All Orders</option>
                            <option value="Pending Payment">Pending Payment</option>

                            {/* In Progress */}
                            <option value="processing">Processing</option>
                            <option value="Preparing for Dispatch">
                                Preparing for Dispatch
                            </option>
                            <option value="PickUP Ready">PickUP Ready</option>
                            <option value="Online shipment booked">
                                Online shipment booked
                            </option>

                            {/* Transit */}
                            <option value="PICK UP SCAN ON FIELD">
                                PICK UP SCAN ON FIELD
                            </option>
                            <option value="SHIPMENT INSCAN">SHIPMENT INSCAN</option>
                            <option value="SHIPMENT OUTSCANNED TO NETWORK">
                                SHIPMENT OUTSCANNED TO NETWORK
                            </option>
                            <option value="SHIPMENT In Transit">SHIPMENT In Transit</option>

                            {/* Completed / Returned */}
                            <option value="Delivered">Delivered</option>
                            <option value="RTO">RTO</option>
                            <option value="Returned">Returned</option>
                        </select>
                    </div>

                    {/* Start Date */}
                    <div style={{ flex: "1 1 180px" }}>
                        <label style={{ fontWeight: "600", color: "#333" }}>
                            Start Date
                        </label>
                        <input
                            type="date"
                            value={startDateFilter}
                            onChange={(e) => setStartDateFilter(e.target.value)}
                            style={{
                                width: "80%",
                                marginTop: "5px",
                                padding: "10px",
                                borderRadius: "6px",
                                border: "1px solid #ccc",
                                fontSize: "14px",
                            }}
                        />
                    </div>

                    {/* End Date */}
                    <div style={{ flex: "1 1 180px" }}>
                        <label style={{ fontWeight: "600", color: "#333" }}>End Date</label>
                        <input
                            type="date"
                            value={endDateFilter}
                            onChange={(e) => setEndDateFilter(e.target.value)}
                            style={{
                                width: "80%",
                                marginTop: "5px",
                                padding: "10px",
                                borderRadius: "6px",
                                border: "1px solid #ccc",
                                fontSize: "14px",
                            }}
                        />
                    </div>
                </div>

                {/* ===== Row 2: Bulk Update + Download ===== */}
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "15px",
                        borderTop: "1px solid #eee",
                        paddingTop: "10px",
                    }}
                >
                    {/* Bulk Update */}
                    <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                        <select
                            value={bulkStatus}
                            onChange={(e) => setBulkStatus(e.target.value)}
                            style={{
                                minWidth: "240px",
                                padding: "10px",
                                borderRadius: "6px",
                                border: "1px solid #ccc",
                                fontSize: "14px",
                                backgroundColor: "#fff",
                                lineHeight: "1.5",
                            }}
                        >
                            <option value="">-- Select Bulk Status --</option>

                            {/* General */}
                            <option value="Pending Payment">Pending Payment</option>

                            {/* In Progress */}
                            <option value="processing">Processing</option>
                            <option value="Preparing for Dispatch">
                                Preparing for Dispatch
                            </option>
                            <option value="PickUP Ready">PickUP Ready</option>
                            <option value="Online shipment booked">
                                Online shipment booked
                            </option>

                            {/* Transit */}
                            <option value="PICK UP SCAN ON FIELD">
                                PICK UP SCAN ON FIELD
                            </option>
                            <option value="SHIPMENT INSCAN">SHIPMENT INSCAN</option>
                            <option value="SHIPMENT OUTSCANNED TO NETWORK">
                                SHIPMENT OUTSCANNED TO NETWORK
                            </option>
                            <option value="SHIPMENT In Transit">SHIPMENT In Transit</option>

                            {/* Completed / Returned */}
                            <option value="Delivered">Delivered</option>
                            <option value="RTO">RTO</option>
                            <option value="Returned">Returned</option>
                        </select>

                        <button
                            onClick={handleBulkUpdate}
                            disabled={selectedOrders.length === 0 || !bulkStatus}
                            style={{
                                padding: "10px 18px",
                                backgroundColor:
                                    selectedOrders.length === 0 || !bulkStatus
                                        ? "#ccc"
                                        : "#4CAF50",
                                color: "#fff",
                                border: "none",
                                borderRadius: "6px",
                                cursor:
                                    selectedOrders.length === 0 || !bulkStatus
                                        ? "not-allowed"
                                        : "pointer",
                                fontWeight: "600",
                                transition: "all 0.3s ease",
                                boxShadow:
                                    selectedOrders.length === 0 || !bulkStatus
                                        ? "none"
                                        : "0 2px 8px rgba(76, 175, 80, 0.3)",
                            }}
                        >
                            Update Selected
                        </button>
                    </div>

                    {/* Download */}
                    <button
                        onClick={() => exportFullOrderDetailsToExcel(filteredOrders)}
                        style={{
                            padding: "10px 18px",
                            backgroundColor: "#007BFF",
                            color: "#fff",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontWeight: "600",
                            transition: "all 0.3s ease",
                        }}
                        onMouseOver={(e) => (e.target.style.backgroundColor = "#0056b3")}
                        onMouseOut={(e) => (e.target.style.backgroundColor = "#007BFF")}
                    >
                        Download Orders
                    </button>
                </div>
            </div>

            {/* Loading Spinner */}
            {loading && <div>Loading...</div>}

            <div style={{ overflowX: "auto", width: "100%" }}>
                <table
                    style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        marginTop: "20px",
                        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                        tableLayout: "auto",
                    }}
                >
                    <thead>
                        <tr>
                            <th
                                style={{
                                    backgroundColor: "#4CAF50",
                                    color: "white",
                                    padding: "12px",
                                    textAlign: "left",
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                    border: "1px solid #ddd",
                                }}
                            >
                                Date
                            </th>
                            <th
                                style={{
                                    backgroundColor: "#4CAF50",
                                    color: "white",
                                    padding: "12px",
                                    textAlign: "left",
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                    border: "1px solid #ddd",
                                }}
                            >
                                Order ID
                            </th>
                            <th
                                style={{
                                    backgroundColor: "#4CAF50",
                                    color: "white",
                                    padding: "12px",
                                    textAlign: "left",
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                    border: "1px solid #ddd",
                                }}
                            >
                                Payment Status
                            </th>
                            <th
                                style={{
                                    backgroundColor: "#4CAF50",
                                    color: "white",
                                    padding: "12px",
                                    textAlign: "left",
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                    border: "1px solid #ddd",
                                }}
                            >
                                Time
                            </th>
                            <th
                                style={{
                                    backgroundColor: "#4CAF50",
                                    color: "white",
                                    padding: "12px",
                                    textAlign: "left",
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                    border: "1px solid #ddd",
                                    width: "40%",
                                }}
                            >
                                Product Info
                            </th>
                            <th
                                style={{
                                    backgroundColor: "#4CAF50",
                                    color: "white",
                                    padding: "12px",
                                    textAlign: "left",
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                    border: "1px solid #ddd",
                                    width: "40%",
                                }}
                            >
                                Product Price
                            </th>
                            <th
                                style={{
                                    backgroundColor: "#4CAF50",
                                    color: "white",
                                    padding: "12px",
                                    textAlign: "left",
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                    border: "1px solid #ddd",
                                    width: "40%",
                                }}
                            >
                                Shipping Cost
                            </th>
                            <th
                                style={{
                                    backgroundColor: "#4CAF50",
                                    color: "white",
                                    padding: "12px",
                                    textAlign: "left",
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                    border: "1px solid #ddd",
                                }}
                            >
                                Coupon Discount
                            </th>
                            <th
                                style={{
                                    backgroundColor: "#4CAF50",
                                    color: "white",
                                    padding: "12px",
                                    textAlign: "left",
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                    border: "1px solid #ddd",
                                }}
                            >
                                Total Price
                            </th>
                            <th
                                style={{
                                    backgroundColor: "#4CAF50",
                                    color: "white",
                                    padding: "12px",
                                    textAlign: "left",
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                    border: "1px solid #ddd",
                                }}
                            >
                                Users
                            </th>
                            <th
                                style={{
                                    backgroundColor: "#4CAF50",
                                    color: "white",
                                    padding: "12px",
                                    textAlign: "left",
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                    border: "1px solid #ddd",
                                }}
                            >
                                Address
                            </th>
                            <th
                                style={{
                                    backgroundColor: "#4CAF50",
                                    color: "white",
                                    padding: "12px",
                                    textAlign: "left",
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                    border: "1px solid #ddd",
                                }}
                            >
                                <div style={{ display: "inline-block", width: "180px" }}>Image</div>
                            </th>


                            <th
                                style={{
                                    backgroundColor: "#4CAF50",
                                    color: "white",
                                    padding: "12px",
                                    textAlign: "left",
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                    border: "1px solid #ddd",
                                }}
                            >
                                Status
                            </th>
                            <th
                                style={{
                                    backgroundColor: "#4CAF50",
                                    color: "white",
                                    padding: "12px",
                                    textAlign: "left",
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                    border: "1px solid #ddd",
                                }}
                            >
                                bulk update status
                            </th>

                            <th
                                style={{
                                    backgroundColor: "#4CAF50",
                                    color: "white",
                                    padding: "12px",
                                    textAlign: "left",
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                    border: "1px solid #ddd",
                                }}
                            >
                                action
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {groupedOrders?.map((order, index) => (
                            <tr key={index}>
                                <td>{formatDate(order?.updatedAt)}</td>
                                <td
                                    style={{
                                        padding: "10px",
                                        textAlign: "left",
                                        fontSize: "18px",
                                        border: "1px solid #ddd",
                                    }}
                                >
                                    {order?.orderID}
                                </td>
                                <td
                                    style={{
                                        padding: "10px",
                                        textAlign: "left",
                                        fontSize: "18px",
                                        border: "1px solid #ddd",
                                    }}
                                >
                                    {order?.paymentStatus}
                                </td>
                                <td
                                    style={{
                                        padding: "10px",
                                        textAlign: "left",
                                        fontSize: "18px",
                                        border: "1px solid #ddd",
                                    }}
                                >
                                    {formatTime(order?.payment?.timestamp)}
                                </td>
                                <td
                                    style={{
                                        padding: "10px",
                                        textAlign: "left",
                                        fontSize: "18px",
                                        border: "1px solid #ddd",
                                    }}
                                >
                                    <table
                                        style={{
                                            width: "100%",
                                            borderCollapse: "collapse",
                                        }}
                                    >
                                        <thead>
                                            <tr>
                                                <th
                                                    style={{
                                                        backgroundColor: "#f2f2f2",
                                                        padding: "8px",
                                                        textAlign: "left",
                                                        fontWeight: "bold",
                                                        fontSize: "14px",
                                                        border: "1px solid #ddd",
                                                    }}
                                                >
                                                    Product ID
                                                </th>
                                                <th
                                                    style={{
                                                        backgroundColor: "#f2f2f2",
                                                        padding: "8px",
                                                        textAlign: "left",
                                                        fontWeight: "bold",
                                                        fontSize: "14px",
                                                        border: "1px solid #ddd",
                                                    }}
                                                >
                                                    Product Name
                                                </th>
                                                <th
                                                    style={{
                                                        backgroundColor: "#f2f2f2",
                                                        padding: "8px",
                                                        textAlign: "left",
                                                        fontWeight: "bold",
                                                        fontSize: "14px",
                                                        border: "1px solid #ddd",
                                                    }}
                                                >
                                                    Quantity
                                                </th>
                                                <th
                                                    style={{
                                                        backgroundColor: "#f2f2f2",
                                                        padding: "8px",
                                                        textAlign: "left",
                                                        fontWeight: "bold",
                                                        fontSize: "14px",
                                                        border: "1px solid #ddd",
                                                    }}
                                                >
                                                    Price
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {order.items.map((item, itemIndex) => (
                                                <tr key={itemIndex}>
                                                    <td
                                                        style={{
                                                            padding: "8px",
                                                            textAlign: "left",
                                                            fontSize: "14px",
                                                            border: "1px solid #ddd",
                                                        }}
                                                    >
                                                        {item.productId || "N/A"}
                                                    </td>
                                                    <td
                                                        style={{
                                                            padding: "8px",
                                                            textAlign: "left",
                                                            fontSize: "14px",
                                                            border: "1px solid #ddd",

                                                            width: "300px",
                                                            minWidth: "300px",
                                                        }}
                                                    >
                                                        {item.title || "N/A"}
                                                    </td>
                                                    <td
                                                        style={{
                                                            padding: "8px",
                                                            textAlign: "left",
                                                            fontSize: "14px",
                                                            border: "1px solid #ddd",
                                                        }}
                                                    >
                                                        {item.quantity}
                                                    </td>
                                                    <td
                                                        style={{
                                                            padding: "8px",
                                                            textAlign: "left",
                                                            fontSize: "14px",
                                                            border: "1px solid #ddd",
                                                        }}
                                                    >
                                                        {item.price}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </td>
                                <td
                                    style={{
                                        padding: "8px",
                                        textAlign: "left",
                                        fontSize: "14px",
                                        border: "1px solid #ddd",
                                    }}
                                >
                                    {order?.items?.reduce((total, item) => total + item.price, 0)}
                                </td>

                                <td
                                    style={{
                                        textAlign: "center",
                                        fontSize: "14px",
                                        border: "1px solid #ddd",
                                    }}
                                >
                                    {order?.totalPrice > 2500 ? 0 : 71}
                                </td>
                                <td
                                    style={{
                                        textAlign: "center",
                                        fontSize: "14px",
                                        border: "1px solid #ddd",
                                    }}
                                >
                                    {order.totalDiscount || "N/A"}
                                </td>

                                <td
                                    style={{
                                        textAlign: "center",
                                        fontSize: "14px",
                                        border: "1px solid #ddd",
                                    }}
                                >
                                    {order.totalPrice || "N/A"}
                                </td>
                                <td
                                    style={{
                                        padding: "10px",
                                        textAlign: "left",
                                        fontSize: "18px",
                                        border: "1px solid #ddd",
                                    }}
                                >

                                    {order?.address?.name}{" "}
                                </td>
                                <td
                                    style={{
                                        padding: "10px",
                                        textAlign: "left",
                                        fontSize: "18px",
                                        border: "1px solid #ddd",
                                    }}
                                >
                                    <div> {order?.address?.email}</div>
                                    <br />
                                    <div> {order?.address?.phone}</div>
                                    <br />
                                    <div>
                                        {order?.address?.location},{" "}
                                        {order?.address?.city}, {order?.address?.state} -{" "}
                                        {order?.address?.pinCode}
                                    </div>
                                </td>
                                <td
                                    style={{
                                        padding: "8px",
                                        textAlign: "center",
                                        fontSize: "14px",
                                        border: "1px solid #ddd",
                                    }}
                                >
                                    {order?.items.map((item, index) => (
                                        <tr key={index}>
                                            <td
                                                style={{
                                                    padding: "8px",
                                                    textAlign: "center",
                                                    fontSize: "14px",
                                                    border: "1px solid #ddd",

                                                }}
                                            >
                                                <img
                                                    src={item.image || "https://via.placeholder.com/150"}
                                                    alt={"Product Image"}
                                                    style={{
                                                        width: "100px",
                                                        height: "100px",
                                                        objectFit: "cover",
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </td>
                                <td
                                    style={{
                                        textAlign: "center",
                                        fontSize: "14px",
                                        border: "1px solid #ddd",
                                    }}
                                >
                                    {order?.status || "N/A"}
                                </td>

                                <td
                                    style={{
                                        textAlign: "center",
                                        fontSize: "14px",
                                        border: "1px solid #ddd",
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedOrders.includes(order?.orderId)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedOrders([...selectedOrders, order?.orderId]);
                                            } else {
                                                setSelectedOrders(
                                                    selectedOrders.filter((id) => id !== order?.orderId)
                                                );
                                            }
                                        }}
                                    />
                                </td>

                                {/* {order?.status == "shipped" || order?.status == "delivered" ? (
                  <></>
                ) : (
                  <td
                    style={{
                      padding: "10px",
                      textAlign: "left",
                      fontsize: "18px",
                      border: "1px solid #ddd",
                    }}
                  >
                    <button
                      onClick={() => handleAccept(order?.orderId)}
                      style={{
                        backgroundColor: "#4CAF50",
                        color: "white",
                        padding: "10px",
                        border: "none",
                        cursor: "pointer",
                        width: "100%",
                      }}
                    >
                      Shipped
                    </button>
                  </td>
                )}

                {order?.status == "processing" ||
                order?.status == "delivered" ? (
                  <></>
                ) : (
                  <td
                    style={{
                      padding: "10px",
                      textAlign: "left",
                      fontsize: "18px",
                      border: "1px solid #ddd",
                    }}
                  >
                    <button
                      onClick={() => handleProcessing(order?.orderId)}
                      style={{
                        backgroundColor: "#ff9800",
                        color: "white",
                        padding: "10px",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      Delivery
                    </button>
                  </td>
                )} */}
                                <td
                                    style={{
                                        padding: "10px",
                                        textAlign: "left",
                                        fontSize: "16px",
                                        border: "1px solid #ddd",
                                    }}
                                >
                                    <select
                                        value={order?.status}
                                        onChange={(e) =>
                                            updateOrderStatus(order?.orderId, e.target.value)
                                        }
                                        style={{
                                            width: "150px",
                                            padding: "10px",
                                            borderRadius: "6px",
                                            border: "1px solid #ccc",
                                            backgroundColor: "#f9f9f9",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <option value=""> -- Update Status -- </option>
                                        <option value="Pending Payment">Pending Payment</option>
                                        <option value="processing">Processing</option>
                                        <option value="Preparing for Dispatch">
                                            Preparing for Dispatch
                                        </option>
                                        <option value="PickUP Ready">PickUP Ready</option>
                                        <option value="Online shipment booked">
                                            Online shipment booked
                                        </option>
                                        <option value="PICK UP SCAN ON FIELD">
                                            PICK UP SCAN ON FIELD
                                        </option>
                                        <option value="SHIPMENT INSCAN">SHIPMENT INSCAN</option>
                                        <option value="SHIPMENT OUTSCANNED TO NETWORK">
                                            SHIPMENT OUTSCANNED TO NETWORK
                                        </option>
                                        <option value="SHIPMENT In Transit">
                                            SHIPMENT In Transit
                                        </option>
                                        <option value="Delivered">Delivered</option>
                                        <option value="RTO">RTO</option>
                                        <option value="Returned">Returned</option>
                                    </select>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default Processing;
